/**
 * @fileoverview Filter-term grammar for date / datetime / time columns
 *
 * A filter term names the components of a value a filter constrains, and nothing else. Those
 * are the components the column's cells actually render — the user's own pattern with the
 * field's precision applied — so a filter matches exactly what looks identical on screen, and
 * precision can never demand more detail than the user's pattern shows.
 *
 * A field configures date and time precision **independently**, so the rendered components do
 * not always form a run from the year down: a datetime showing month and time (`05 14:30`)
 * constrains month, hour and minute while leaving year and day free. Components the cells omit
 * in the middle are therefore written as `X` (the "unspecified digit" of ISO 8601-2 / EDTF), and
 * only unconstrained *trailing* components are dropped:
 *
 *   2026-05-14T15:30+03:00[Europe/Riga]   every component down to the minute
 *   2026-05                               a month; day and finer are not part of the value
 *   XXXX-05                               May of any year
 *   XXXX-05-XXT15:30+03:00[Europe/Riga]   15:30 on any day of any May
 *
 * A term with no `X` denotes one contiguous period, which the backend widens into
 * `[start, end)`; a masked one denotes a period that repeats, which it compares component-wise.
 * Sending either as a single value is what lets "equals" travel as one EQ condition instead of a
 * GTE+LTE pair. Both forms are read by `internal/data` (timestamp_period.go,
 * masked_timestamp.go), which keeps the same canonical component order as here.
 *
 * `datetime` terms carry the browser's zone, since the stored value is a real instant while cells
 * render in local time: without it the period would be cut on the server's zone and a row
 * displayed as `01.01.2026` could fall outside "2026". The zone is written RFC 9557 style, offset
 * plus IANA name — a masked period repeats across DST, so only the name renders every occurrence
 * at the clock time the cell shows. Calendar dates and times of day carry no zone — they have
 * none.
 *
 * date-fns `format` is used with locale-independent tokens only (serialization), never for
 * user-facing text.
 */

import { format as formatWithDateFns } from "date-fns";

import { formatSections, parseFormatParts } from "../../DatetimePicker/dateSectionUtils";
import type { DateFilterKind } from "../types";

/** Canonical order, most significant first — shared with the backend grammar. */
export type TermComponent = "year" | "month" | "day" | "hour" | "minute" | "second" | "millisecond";

/** The components a column's cells render, and so the components its filter constrains. */
export type TermShape = ReadonlySet<TermComponent>;

const DATE_COMPONENTS = ["year", "month", "day"] as const satisfies readonly TermComponent[];
const TIME_COMPONENTS = ["hour", "minute", "second", "millisecond"] as const satisfies readonly TermComponent[];

export const TERM_WIDTHS: Record<TermComponent, number> = {
  year: 4,
  month: 2,
  day: 2,
  hour: 2,
  minute: 2,
  second: 2,
  millisecond: 3,
};

/** Section types of the masked editor's pattern engine, mapped onto term components. */
const SECTION_COMPONENTS: Record<string, TermComponent> = {
  year: "year",
  month: "month",
  day: "day",
  hours: "hour",
  minutes: "minute",
  seconds: "second",
  milliseconds: "millisecond",
};

const MASK_CHAR = "X";
const ZONE_SUFFIX = /(Z|[+-]\d{2}:\d{2})(?:\[([A-Za-z0-9_+\-/]+)\])?$/;

/**
 * The zone a datetime term carries, in the RFC 9557 form the backend reads: the numeric offset
 * plus the IANA name in brackets. The name is what a masked term needs — such a term spans both
 * sides of a DST transition, and one fixed offset would render half of its occurrences an hour
 * away from the clock time the cell shows. The offset stays for servers that cannot resolve the
 * name, and for terms that name a single contiguous period.
 */
function termZone(date: Date): string {
  const offset = formatWithDateFns(date, "xxx");
  const name = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return name ? `${offset}[${name}]` : offset;
}

/**
 * The components a column displays, read off the rendered patterns (precision already applied)
 * rather than the precision enums — that way a user pattern of `HH:mm` caps a seconds-precision
 * field at minutes, exactly as the cell does.
 */
export function resolveTermShape(
  kind: DateFilterKind,
  resolvedDateFormat: string,
  resolvedTimeFormat: string,
): TermShape {
  const shape = new Set<TermComponent>();
  const collect = (pattern: string, allowed: readonly TermComponent[]) => {
    for (const section of formatSections(parseFormatParts(pattern))) {
      const component = SECTION_COMPONENTS[section.type];
      if (component && allowed.includes(component)) shape.add(component);
    }
  };
  if (kind !== "time") collect(resolvedDateFormat, DATE_COMPONENTS);
  if (kind !== "date") collect(resolvedTimeFormat, TIME_COMPONENTS);
  return shape;
}

/** Whether the column shows a time of day, and so whether its editor needs time sections. */
export function shapeHasTime(shape: TermShape): boolean {
  return TIME_COMPONENTS.some((component) => shape.has(component));
}

function lastShownIndex(components: readonly TermComponent[], shape: TermShape): number {
  let last = -1;
  components.forEach((component, index) => {
    if (shape.has(component)) last = index;
  });
  return last;
}

/** Serializes a picked date into the term for `shape`. */
export function dateToFilterTerm(date: Date, kind: DateFilterKind, shape: TermShape): string {
  const values: Record<TermComponent, number> = {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    millisecond: date.getMilliseconds(),
  };
  const segment = (component: TermComponent) =>
    shape.has(component)
      ? String(values[component]).padStart(TERM_WIDTHS[component], "0")
      : MASK_CHAR.repeat(TERM_WIDTHS[component]);

  const timePart = renderTimePart(shape, segment);
  if (kind === "time") return timePart;

  // A time part fixes the width of what precedes it, so every date component is written (masked
  // where the cells omit it). Without one, unconstrained trailing components are just dropped.
  const shownDate = lastShownIndex(DATE_COMPONENTS, shape);
  const datePart = (timePart ? DATE_COMPONENTS : DATE_COMPONENTS.slice(0, shownDate + 1)).map(segment).join("-");
  if (!datePart) return "";

  const parts = [datePart];
  if (timePart) parts.push("T", timePart);
  if (kind === "datetime") parts.push(termZone(date));
  return parts.join("");
}

function renderTimePart(shape: TermShape, segment: (component: TermComponent) => string): string {
  const shown = lastShownIndex(TIME_COMPONENTS, shape);
  if (shown < 0) return "";
  let rendered = segment("hour");
  if (shown >= 1) rendered += `:${segment("minute")}`;
  if (shown >= 2) rendered += `:${segment("second")}`;
  if (shown >= 3) rendered += `.${segment("millisecond")}`;
  return rendered;
}

export type ComponentValues = Partial<Record<TermComponent, number>>;

export interface ParsedTerm {
  values: ComponentValues;
  /** Numeric offset, or "Z". */
  zone: string;
  /** IANA name; the editor ignores it, a masked term compared in the browser needs it across DST. */
  zoneName: string;
  /** Some component is free, so the term names a repeating period rather than one instant. */
  masked: boolean;
}

const isMasked = (segment: string) => /^X+$/.test(segment);

/** Splits a term into the values it constrains. Null for anything not term-shaped. */
export function parseTerm(term: string, kind: DateFilterKind): ParsedTerm | null {
  const zoneMatch = kind === "time" ? null : ZONE_SUFFIX.exec(term);
  const zone = zoneMatch?.[1] ?? "";
  const zoneName = zoneMatch?.[2] ?? "";
  const body = zoneMatch ? term.slice(0, zoneMatch.index) : term;
  const [datePart, timePart = ""] = kind === "time" ? ["", body] : body.split("T");

  const values: ComponentValues = {};
  let masked = false;

  const assign = (component: TermComponent | undefined, segment: string): boolean => {
    if (!component) return false;
    if (isMasked(segment)) {
      masked = true;
      return true;
    }
    if (!/^\d+$/.test(segment)) return false;
    values[component] = Number(segment);
    return true;
  };

  if (kind !== "time") {
    const segments = datePart ? datePart.split("-") : [];
    if (!segments.length) return null;
    // A lone one- or two-digit date segment is a bare month, the encoding that predates masking.
    const components: readonly TermComponent[] =
      segments.length === 1 && segments[0].length <= 2 ? ["month"] : DATE_COMPONENTS;
    if (segments.length > components.length) return null;
    for (const [index, segment] of segments.entries()) {
      if (!assign(components[index], segment)) return null;
    }
  }

  if (timePart) {
    const segments = timePart.split(/[:.]/);
    if (segments.length > TIME_COMPONENTS.length) return null;
    for (const [index, segment] of segments.entries()) {
      if (!assign(TIME_COMPONENTS[index], segment)) return null;
    }
  }

  return { values, zone, zoneName, masked };
}

const pad = (value: number, length = 2) => String(value).padStart(length, "0");

/**
 * Reads a term back into a date the editor can show, defaulting the components it leaves free.
 * Also accepts the full ISO instants and `HH:mm:ss` values stored by older filters, so a saved
 * filter still opens. Returns null for anything unparseable.
 */
export function filterTermToDate(term: string, kind: DateFilterKind): Date | null {
  if (!term) return null;

  const parsed = parseTerm(term, kind);
  if (!parsed) {
    // Not term-shaped: a legacy value the platform can still parse on its own.
    const fallback = new Date(term);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  const { values, zone, masked } = parsed;
  const year = values.year ?? new Date().getFullYear();
  const month = values.month ?? 1;
  const day = values.day ?? 1;
  const hour = values.hour ?? 0;
  const minute = values.minute ?? 0;
  const second = values.second ?? 0;
  const millisecond = values.millisecond ?? 0;

  // The offset belongs to the instant the user picked, so it only reconstructs a term that names
  // one. A masked term's free components are filled in from an arbitrary date, where that offset
  // may no longer be in force — reading its clock time locally is what keeps the editor showing
  // the components the term actually carries.
  const isoLocal = `${pad(year, 4)}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
  const composed =
    zone && !masked
      ? new Date(`${isoLocal}.${pad(millisecond, 3)}${zone}`)
      : new Date(year, month - 1, day, hour, minute, second, millisecond);
  return isNaN(composed.getTime()) ? null : composed;
}

/** Re-encodes any accepted term (current, foreign or legacy) at the column's own shape. */
export function normalizeFilterTerm(term: string, kind: DateFilterKind, shape: TermShape): string {
  const date = filterTermToDate(term, kind);
  return date ? dateToFilterTerm(date, kind, shape) : "";
}
